import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { useToast } from '@/components/ui/use-toast';
import { Building2, Shirt, Loader2, Save } from 'lucide-react';
import { fetchInventarioVerticalByEdificio } from '@/services/inventarioLenceriaVertical.service';
import { updateEntregaVertical } from '@/services/entregasLenceriaVertical.service';

/**
 * Modal para editar un alistamiento que aún NO está en ruta (estado 'alistado').
 * Permite ajustar las cantidades de prendas y las observaciones.
 */
const EditarAlistamientoModal = ({ isOpen, onClose, onSuccess, alistamiento }) => {
  const { toast } = useToast();
  const [prendasDisponibles, setPrendasDisponibles] = useState([]);
  const [itemsSeleccionados, setItemsSeleccionados] = useState([]);
  const [observaciones, setObservaciones] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingPrendas, setIsLoadingPrendas] = useState(false);

  // Cargar inventario y precargar los items del alistamiento al abrir
  useEffect(() => {
    if (isOpen && alistamiento) {
      setObservaciones(alistamiento.observaciones || '');
      // Precargar los items existentes como seleccionados
      const iniciales = (alistamiento.items || []).map(item => ({
        prenda_id: item.id,
        nombre: item.nombre,
        peso: item.peso || 0,
        cantidad: item.cantidad || 0,
      }));
      setItemsSeleccionados(iniciales);
      loadPrendas();
    } else if (!isOpen) {
      setPrendasDisponibles([]);
      setItemsSeleccionados([]);
      setObservaciones('');
    }
  }, [isOpen, alistamiento]);

  const loadPrendas = async () => {
    const edificioId = alistamiento?.edificio?.id;
    if (!edificioId) {
      setPrendasDisponibles([]);
      return;
    }

    setIsLoadingPrendas(true);
    try {
      const response = await fetchInventarioVerticalByEdificio(edificioId);
      const inventarioItems = (response && response.data) ? response.data : [];

      const prendasConStock = inventarioItems.map(item => ({
        _id: item.tipo_prenda?.id || item._id,
        nombre: item.tipo_prenda?.nombre || 'Sin nombre',
        peso: item.tipo_prenda?.peso || 0,
        stock_disponible: item.cantidad_disponible || 0,
      }));

      // Asegurar que las prendas ya incluidas en el alistamiento estén en la lista,
      // aunque no aparezcan en el inventario actual.
      (alistamiento.items || []).forEach(item => {
        if (!prendasConStock.some(p => p._id === item.id)) {
          prendasConStock.push({
            _id: item.id,
            nombre: item.nombre,
            peso: item.peso || 0,
            stock_disponible: 0,
          });
        }
      });

      setPrendasDisponibles(prendasConStock);
    } catch (error) {
      console.error('Error al cargar inventario vertical:', error);
      toast({
        title: 'Error',
        description: 'No se pudo cargar el inventario del edificio',
        variant: 'destructive'
      });
      setPrendasDisponibles([]);
    } finally {
      setIsLoadingPrendas(false);
    }
  };

  const handleCantidadChange = (prendaId, nuevaCantidad) => {
    const cantidad = parseInt(nuevaCantidad) || 0;

    if (cantidad === 0) {
      setItemsSeleccionados(prev => prev.filter(item => item.prenda_id !== prendaId));
      return;
    }

    const prenda = prendasDisponibles.find(p => p._id === prendaId);
    if (!prenda) return;

    setItemsSeleccionados(prev => {
      const existe = prev.find(item => item.prenda_id === prendaId);
      if (existe) {
        return prev.map(item =>
          item.prenda_id === prendaId ? { ...item, cantidad } : item
        );
      }
      return [
        ...prev,
        { prenda_id: prenda._id, nombre: prenda.nombre, peso: prenda.peso || 0, cantidad },
      ];
    });
  };

  const getCantidadActual = (prendaId) => {
    const item = itemsSeleccionados.find(item => item.prenda_id === prendaId);
    return item ? item.cantidad : '';
  };

  const totalItems = itemsSeleccionados.reduce((sum, item) => sum + item.cantidad, 0);
  const pesoTotal = itemsSeleccionados.reduce((sum, item) => sum + (item.peso * item.cantidad), 0);

  const handleGuardar = async () => {
    const itemsConCantidad = itemsSeleccionados.filter(item => item.cantidad > 0);
    if (itemsConCantidad.length === 0) {
      toast({
        title: 'Error',
        description: 'Debe mantener al menos una prenda con cantidad',
        variant: 'destructive'
      });
      return;
    }

    setIsLoading(true);
    try {
      const data = {
        items: itemsConCantidad.map(item => ({
          id: item.prenda_id,
          nombre: item.nombre,
          cantidad: item.cantidad,
          peso: item.peso,
          tipo: 'lenceria',
          observacion: ''
        })),
        total_items: totalItems,
        peso_total: pesoTotal,
        observaciones: observaciones || 'Entrega directa desde inventario vertical',
      };

      await updateEntregaVertical(alistamiento._id, data);

      toast({
        title: 'Éxito',
        description: 'Alistamiento actualizado correctamente',
      });

      onClose();
      if (onSuccess) onSuccess();
    } catch (error) {
      console.error('Error al actualizar alistamiento:', error);
      toast({
        title: 'Error',
        description: error.message || 'No se pudo actualizar el alistamiento',
        variant: 'destructive'
      });
    } finally {
      setIsLoading(false);
    }
  };

  if (!alistamiento) return null;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto p-0 gap-0">
        <DialogHeader className="p-6 border-b bg-gradient-to-r from-blue-50 to-purple-50 dark:from-blue-900/20 dark:to-purple-900/20">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-blue-100 dark:bg-blue-900/30 rounded-lg">
              <Shirt className="h-6 w-6 text-blue-600 dark:text-blue-400" />
            </div>
            <div>
              <DialogTitle className="text-xl font-bold text-gray-900 dark:text-white">
                Editar Alistamiento
              </DialogTitle>
              <DialogDescription className="text-sm text-gray-600 dark:text-gray-400">
                Ajusta las prendas antes de ponerlo en ruta
                {alistamiento.solicitud_id ? ` · ${alistamiento.solicitud_id}` : ''}
              </DialogDescription>
            </div>
          </div>
        </DialogHeader>

        <div className="space-y-6 p-6">
          {/* Edificio (bloqueado) */}
          <div className="p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-800">
            <div className="flex items-center gap-3">
              <Building2 className="h-5 w-5 text-blue-600" />
              <div>
                <div className="font-medium text-gray-900 dark:text-white">
                  {alistamiento.edificio?.nombre}
                </div>
                <div className="text-sm text-gray-600 dark:text-gray-400">
                  {alistamiento.edificio?.ciudad} {alistamiento.edificio?.direccion ? `• ${alistamiento.edificio.direccion}` : ''}
                </div>
              </div>
            </div>
          </div>

          {/* Prendas */}
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <label className="flex items-center gap-2 text-sm font-semibold text-gray-700 dark:text-gray-300">
                <Shirt className="h-4 w-4 text-purple-600" />
                Prendas <span className="font-normal text-gray-400">— ajusta cantidades</span>
              </label>
              {itemsSeleccionados.length > 0 && (
                <Badge className="bg-blue-100 text-blue-700 hover:bg-blue-100">
                  {itemsSeleccionados.length} prenda(s)
                </Badge>
              )}
            </div>

            {isLoadingPrendas ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="h-6 w-6 animate-spin text-blue-600" />
              </div>
            ) : (
              <div className="border rounded-lg max-h-[400px] overflow-y-auto">
                {prendasDisponibles.length === 0 ? (
                  <div className="p-4 text-center text-gray-500">
                    No hay prendas disponibles para este edificio
                  </div>
                ) : (
                  <div className="divide-y">
                    {prendasDisponibles.map((prenda) => {
                      const cantidadActual = getCantidadActual(prenda._id);
                      const tieneSeleccion = cantidadActual > 0;

                      return (
                        <div
                          key={prenda._id}
                          className={`p-4 transition-colors ${tieneSeleccion ? 'bg-blue-50' : 'hover:bg-gray-50'}`}
                        >
                          <div className="flex items-center gap-4">
                            <div className="flex-1">
                              <div className="font-medium text-sm text-gray-900">{prenda.nombre}</div>
                              <span className="text-xs text-gray-500">{prenda.peso}g por unidad</span>
                            </div>
                            <div className="flex flex-col items-end">
                              <Input
                                type="number"
                                min="0"
                                value={cantidadActual || ''}
                                onChange={(e) => handleCantidadChange(prenda._id, e.target.value)}
                                onFocus={(e) => e.target.select()}
                                placeholder="0"
                                className={`h-10 w-24 text-center font-medium ${tieneSeleccion ? 'border-blue-500 bg-white' : ''}`}
                              />
                              {tieneSeleccion && (
                                <span className="text-xs text-blue-600 mt-1">
                                  {(prenda.peso * cantidadActual / 1000).toFixed(2)} kg
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            )}

            {/* Resumen */}
            {itemsSeleccionados.length > 0 && (
              <div className="grid grid-cols-2 gap-3">
                <div className="flex items-center justify-between p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-800">
                  <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Total Items</span>
                  <span className="text-xl font-bold text-blue-600">{totalItems}</span>
                </div>
                <div className="flex items-center justify-between p-4 bg-purple-50 dark:bg-purple-900/20 rounded-lg border border-purple-200 dark:border-purple-800">
                  <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Peso Total</span>
                  <span className="text-xl font-bold text-purple-600">{(pesoTotal / 1000).toFixed(2)} kg</span>
                </div>
              </div>
            )}
          </div>

          {/* Observaciones */}
          <div className="space-y-2">
            <label className="text-sm font-semibold text-gray-700 dark:text-gray-300">
              Observaciones <span className="font-normal text-gray-400">(opcional)</span>
            </label>
            <Textarea
              value={observaciones}
              onChange={(e) => setObservaciones(e.target.value)}
              placeholder="Agregar observaciones sobre el alistamiento..."
              rows={3}
            />
          </div>
        </div>

        <DialogFooter className="p-6 border-t bg-gray-50 dark:bg-gray-900">
          <Button variant="outline" onClick={onClose} disabled={isLoading}>
            Cancelar
          </Button>
          <Button
            onClick={handleGuardar}
            disabled={isLoading || itemsSeleccionados.length === 0}
            className="bg-blue-600 hover:bg-blue-700"
          >
            {isLoading ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Guardando...
              </>
            ) : (
              <>
                <Save className="h-4 w-4 mr-2" />
                Guardar Cambios
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default EditarAlistamientoModal;
